import { useMemo } from 'react';
import { useEventWebsiteGuestQuery } from '@apps/guest/GuestSite.graphql.generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { EventDesignPurpose } from '@graphql/generated-base';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useAuthUpgradeRefetch } from '@shared/hooks/useAuthUpgradeQuery';
import { useFetchWebsitePageListData } from '@shared/hooks/useWebsitePages';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { shouldUseEventDesignDraft } from '@shared/utils/eventDesignPurpose';
import { isInIframe } from '@shared/utils/isInIframe';
import { useIsGuestSiteSsrEnabled } from './useIsGuestSiteSsrEnabled';
import type { EventWebsiteGuestMergedQuery } from './useEventWebsiteGuestData.types';

type UseEventWebsiteGuestDataArgs = Readonly<{
  eventHandle: string;
}>;

const isPreviewing = isInIframe();

export const useEventWebsiteGuestData = (args: UseEventWebsiteGuestDataArgs) => {
  const { eventHandle } = args;
  const { isAdmin, hasIdentifiedUserOnce } = useEventUserRole();
  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const isWebsitePublishCapabilityEnabled = websitePublishCapabilityFeature.eventExposure.value;

  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();
  // PLAN-17: read cache-first on the server render (cache is prewarmed) to avoid a redundant
  // EWG fetch; client keeps cache-and-network for the auth-upgrade flow.
  const [isConsolidatedSsrEnabled] = useIsFeatureEnabled('guestSiteConsolidatedSsrQuery');
  const isServerRender = typeof window === 'undefined';

  const shouldUseDraft = !isWebsitePublishCapabilityEnabled && shouldUseEventDesignDraft();
  const eventDesignPurpose = shouldUseDraft ? EventDesignPurpose.draft : EventDesignPurpose.live;
  const isAdminDashboard = isPreviewing && isAdmin;
  const skip = !eventHandle || (!isGuestSiteSsrEnabled && !hasIdentifiedUserOnce);

  const { data, loading, error, refetch } = useEventWebsiteGuestQuery({
    variables: {
      eventHandle,
      eventDesignPurpose,
      isAdminDashboard,
    },
    batchMode: 'off',
    // cache-and-network serves cached SSR data while useAuthUpgradeRefetch fetches fresh
    // authenticated data. PLAN-17: server render reads cache-first off the prewarmed cache.
    fetchPolicy: isConsolidatedSsrEnabled && isServerRender ? 'cache-first' : isGuestSiteSsrEnabled ? 'cache-and-network' : 'cache-first',
    ssr: true,
    skip,
  });

  // pages + pageContainers now live in WebsitePageList (see PLAN-16 §7). Merge them
  // back into the EventWebsiteGuest shape so consumers reading event.pages /
  // event.pageContainers keep working unchanged.
  const {
    data: pageListData,
    loadingPages,
    pagesError,
    refetchPages,
  } = useFetchWebsitePageListData(eventHandle, {
    forceAdmin: isAdminDashboard,
    skip,
  });

  // Use sub-field references (not pageListData itself) as deps. Apollo guarantees
  // array-reference stability when contents don't change, so this keeps the merged
  // result reference-stable across unrelated cache emissions — important because
  // GuestSite.controller's eventPreview-seeding effect depends on this reference.
  const pages = pageListData?.eventByName?.pages;
  const pageContainers = pageListData?.eventByName?.pageContainers;

  const mergedData = useMemo((): EventWebsiteGuestMergedQuery | undefined => {
    if (!data) return undefined;
    if (!data.eventByName) return data as EventWebsiteGuestMergedQuery;
    return {
      ...data,
      eventByName: {
        ...data.eventByName,
        pages: pages ?? [],
        pageContainers: pageContainers ?? [],
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pages, pageContainers]);

  // SSR public-first: After GetMe resolves client-side, refetch with auth token.
  // Still needed post EWG-split: pages/pagesInfo are session-gated server-side
  // (locked guests get a filtered list), so unlocked guests need the authed refetch.
  // The authLink in client.ts automatically attaches the token on refetch.
  useAuthUpgradeRefetch(isGuestSiteSsrEnabled ? refetch : undefined);

  return {
    data: mergedData,
    // Loading combines EWG with WebsitePageList's INITIAL fetch only (when pageListData
    // hasn't arrived yet). After initial load, subsequent refetches of WebsitePageList
    // don't flicker `loading` true — preventing widget unmounts on schedule/accommodation
    // mutations while still surfacing readiness so callers don't render with empty pages
    // mid-initial-fetch.
    loading: loading || (loadingPages && !pageListData),
    // Surface page-list failures so callers don't silently treat a missing pages array
    // as a successful empty result (e.g., misroute / hide guest content). EWG errors
    // still take precedence; the page-list error reports independently.
    error: error ?? pagesError,
    refetch,
    // Active-observer refetch for WebsitePageList. Use this (not
    // `client.refetchQueries({ include: ['WebsitePageList'] })`) so we don't wake the
    // skip-guarded prefetch instance in usePrefetchSsrQueries.
    refetchPages,
  };
};
